import { toSafeString } from './toSafeString.js';

// Tools that act on the workflow as a whole. One entry per workflow tool the API defines
// (buildWorkflowAuthoringToolDefinitions.js) that does not name a step.
const WORKFLOW_TOOL_CALL_LABELS = Object.freeze({
  workflow_requirements_start: { running: 'Starting workflow planning', completed: 'Started workflow planning' },
  workflow_plan_manifest_update: { running: 'Updating plan details', completed: 'Updated plan details' },
  workflow_actions_needed_update: { running: 'Updating what Pieve needs from you', completed: 'Updated what Pieve needs from you' },
  workflow_plan_amendment_propose: { running: 'Proposing a plan change', completed: 'Proposed a plan change' },
  workflow_create_from_thread: { running: 'Creating the workflow', completed: 'Created the workflow' },
  workflow_layout_get: { running: 'Reading the workflow', completed: 'Read the workflow' },
  workflow_validate: { running: 'Checking the workflow', completed: 'Checked the workflow' },
  workflow_publish: { running: 'Publishing the workflow', completed: 'Published the workflow' },
  workflow_activate: { running: 'Turning the workflow on', completed: 'Turned the workflow on' },
  workflow_versions_list: { running: 'Checking earlier versions of the workflow', completed: 'Checked earlier versions of the workflow' },
  run_workflow: { running: 'Running the workflow', completed: 'Ran the workflow' },
  workflow_run_get: { running: 'Checking the workflow run', completed: 'Checked the workflow run' },
  workflow_test_prepare: { running: 'Preparing the test', completed: 'Prepared the test' }
});
// `workflow_activate` is also how a workflow is paused (`active: false`), and "Turned the workflow on" would say
// the opposite of what happened.
const WORKFLOW_PAUSE_TOOL_CALL_LABEL = Object.freeze({ running: 'Pausing the workflow', completed: 'Paused the workflow' });

// Tools that act on one step. `summary` is the verb a folded group counts ("Added 5 steps and updated 1
// step"); reads and lookups are not counted, because a group is about what changed.
export const WORKFLOW_STEP_TOOL_CALL_VERBS = Object.freeze({
  workflow_step_create: { running: 'Adding', completed: 'Added', article: 'a', summary: 'add' },
  workflow_steps_create: { running: 'Adding', completed: 'Added', article: 'a', summary: 'add' },
  workflow_step_update: { running: 'Updating', completed: 'Updated', article: 'the', summary: 'update' },
  workflow_step_run: { running: 'Running', completed: 'Ran', article: 'the', summary: 'run' },
  workflow_step_delete: { running: 'Deleting', completed: 'Deleted', article: 'the', summary: 'delete' },
  workflow_step_move: { running: 'Moving', completed: 'Moved', article: 'the', summary: 'move' },
  workflow_step_reorder: { running: 'Moving', completed: 'Moved', article: 'the', summary: 'move' },
  workflow_step_get: { running: 'Reading', completed: 'Read', article: 'the', summary: null },
  workflow_step_output_get: { running: 'Reading the output of', completed: 'Read the output of', article: 'the', summary: null },
  workflow_run_step_get: { running: 'Reading the run log of', completed: 'Read the run log of', article: 'the', summary: null },
  workflow_step_resolve_app_version: { running: 'Updating the app used by', completed: 'Updated the app used by', article: 'the', summary: null },
  workflow_step_get_contract: { running: 'Checking what is needed for', completed: 'Checked what is needed for', article: 'the', summary: null }
});

/* A workflow tool's label from its name alone, for the surfaces that do not hold the workflow's draft: the share
   page, the thread review, and the feed before the draft loads. A step tool says "a step"; the feed's workflow row
   names the step and its app from the draft (buildWorkflowToolCallDisplay). Pieve's own runs keep the model's
   arguments under `request.parsedArgs`; a plan run keeps them as the call's input. */
const resolvePieveWorkflowToolCallDisplayText = ({ toolName, args = {}, completed = false } = {}) => {
  const normalizedToolName = toSafeString(toolName).toLowerCase();
  const verb = WORKFLOW_STEP_TOOL_CALL_VERBS[normalizedToolName];

  if (verb) {
    return `${completed ? verb.completed : verb.running} a step`;
  }

  const active = args?.request?.parsedArgs?.active ?? args?.active;
  const labels = normalizedToolName === 'workflow_activate' && active === false ? WORKFLOW_PAUSE_TOOL_CALL_LABEL : WORKFLOW_TOOL_CALL_LABELS[normalizedToolName];

  if (!labels) {
    return null;
  }

  return completed ? labels.completed : labels.running;
};

export default resolvePieveWorkflowToolCallDisplayText;
